import moment from "moment"

/**
 * This function will resize the columns to fit the grid
 * @param gridRef - A reference to the grid component.
 */
export const onFitColumnsWidth = gridRef => {
  if (gridRef && gridRef?.api) {
    gridRef?.api.sizeColumnsToFit()
  }
}

/**
 * Given an ETL column type, return the corresponding column type
 * @param type - The type of the column.
 * @returns columnType
 */
const getColumnType = (type = "") => {
  switch (type) {
    case "Timestamp":
    case "Date":
    case "DateTime":
      return "date"
    case "Integer":
    case "Number":
      return "number"
    case "String":
    default:
      return "text"
  }
}
/**
 * Check date format and format as needed
 * @param value
 * @returns Formatted Date
 */
export const formatDateCell = value => {
  // Checking value format to render a good date format
  if (moment(value, "x", true).isValid() && !moment(value, "YYYY", true).isValid())
    return moment(value, "x").format("DD/MM/YYYY")
  else if (moment(value, "YYYY-MM-DD", true).isValid())
    return moment(value, "YYYY-MM-DD").format("DD/MM/YYYY")
  else if (moment(value, "YYYY-MM", true).isValid())
    return moment(value, "YYYY-MM").format("MM/YYYY")
  else if (moment(value, "YYYY", true).isValid()) return moment(value, "YYYY").format("YYYY")
  else return value
}

/**
 * Given a value, a type and an options object, return the value formatted as a string
 * @param value - the value to format
 * @param type - the type of the cell. Can be 'date', 'number' or 'text'
 * @param options - numberDecimal, showSpaceForThousand, displayPercentSymbolAfterValue
 * @returns the value of the cell formatted according to the type of the cell.
 */
export const getCellFormat = (value, type, options = {}) => {
  const {
    numberDecimal = 0,
    showSpaceForThousand = true,
    displayPercentSymbolAfterValue = false,
    // Colonne exprimée en ratio 0–1 à afficher en pourcentage : multiplie la valeur par 100
    // et suffixe "%" (ex. taux moyen 0.0137 → "1%"). Distinct de displayPercentSymbolAfterValue
    // qui n'ajoute que le symbole sans changer l'échelle.
    displayAsPercent = false,
  } = options

  // if (type === "date") {
  //   return formatDateCell(value)
  // } else

  if (type === "number") {
    if ([undefined, null].includes(value)) return value
    if (typeof value !== "number") return value

    const showPercentSymbol = displayPercentSymbolAfterValue || displayAsPercent
    const numericValue = displayAsPercent ? value * 100 : value
    var valueToReturn = numericValue
    if (showSpaceForThousand) {
      valueToReturn = numericValue.toLocaleString("fr", {
        minimumFractionDigits: numberDecimal,
        maximumFractionDigits: numberDecimal,
      })
    } else valueToReturn = numericValue.toFixed(numberDecimal)
    if (showPercentSymbol) {
      valueToReturn = `${valueToReturn}%`
    }
    return `${valueToReturn}`.replace(".", ",")
  } else if (type === "text" && displayPercentSymbolAfterValue) {
    if ([undefined, null].includes(value)) return value
    return `${value}%`
  }
}

/**
 * It returns the filter format for the column type.
 * @param type - The type of filter to use.
 * @returns The filter format for the column.
 */
const getFilterFormat = type => {
  switch (type) {
    case "number":
      return "agNumberColumnFilter"
    case "text":
      return "agTextColumnFilter"
    default:
      return ""
  }
}

const date_regex = /^\d{2}\/\d{2}\/\d{4}$/

export const strLookLikeDDMMYYYYDate = testdate => date_regex.test(testdate)

// export const getDDMMYYYYWeight = date => {
//   if (date === undefined || date === null || date.length !== 10) return null

//   var yearNumber = parseInt(date.substring(6, 10))
//   var monthNumber = parseInt(date.substring(3, 5))
//   var dayNumber = parseInt(date.substring(0, 2))
//   return yearNumber * 10000 + monthNumber * 100 + dayNumber
// }

export const moisEnLettres = {
  janvier: 1,
  février: 2,
  mars: 3,
  avril: 4,
  mai: 5,
  juin: 6,
  juillet: 7,
  août: 8,
  aout: 8,
  septembre: 9,
  octobre: 10,
  novembre: 11,
  décembre: 12,
}

export const getDDMMYYYYWeight = date => {
  if (!date) return null

  console.log("getDDMMYYYYWeight", date)
  // Si c'est un nombre, vérifier si c'est un timestamp
  if (typeof date === "number") {
    console.log("getDDMMYYYYWeight - number", date)
    // 🟢 Vérifier si c'est un timestamp (10 ou 13 chiffres)
    if (date.toString().length === 10) {
      date = date * 1000 // Convertir un timestamp en secondes vers millisecondes
    }

    let dateObj = new Date(date)
    let year = dateObj.getFullYear()
    let month = dateObj.getMonth() + 1 // getMonth() commence à 0
    let day = dateObj.getDate()
    return year * 10000 + month * 100 + day // YYYYMMDD
  }

  date = date.toLowerCase().trim() // Normalisation

  console.log("getDDMMYYYYWeight", date)

  // 🟢 Cas "YYYY" (ex: "2024")
  if (/^\d{4}$/.test(date)) {
    return parseInt(date) * 10000 // YYYY0000
  }

  // 🟢 Cas "MM/YYYY" (ex: "05/2024")
  if (/^\d{2}\/\d{4}$/.test(date)) {
    let [month, year] = date.split("/").map(Number)
    return year * 10000 + month * 100 // YYYYMM00
  }

  // 🟢 Cas "DD/MM" (ex: "12/05") → On met l'année par défaut 2000
  if (/^\d{2}\/\d{2}$/.test(date)) {
    let [day, month] = date.split("/").map(Number)
    return 2000 * 10000 + month * 100 + day // 2000MMDD
  }

  // 🟢 Cas "DD/MM/YYYY" (ex: "12/05/2024")
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    let [day, month, year] = date.split("/").map(Number)
    return year * 10000 + month * 100 + day // YYYYMMDD
  }

  // 🟢 Cas "Mois YYYY" (ex: "Mai 2024")
  let moisAnneeMatch = date.match(/^([a-zéû]+) (\d{4})$/)
  if (moisAnneeMatch) {
    let moisNom = moisAnneeMatch[1]
    let annee = parseInt(moisAnneeMatch[2])
    let moisNum = moisEnLettres[moisNom]
    if (moisNum) return annee * 10000 + moisNum * 100 // YYYYMM00
  }

  // 🟢 Cas "Mois" seul (ex: "Avril")
  if (moisEnLettres[date]) {
    return 2000 * 10000 + moisEnLettres[date] * 100 // 2000MM00 (année par défaut)
  }

  // 🟢 Cas "Jour X" (ex: "Jour 1")
  let jourMatch = date.match(/^jour (\d+)$/)
  if (jourMatch) {
    return 2000 * 10000 + 1 * 100 + parseInt(jourMatch[1]) // 200001DD
  }

  return null // Si on ne peut pas interpréter la date
}

export const dateComparator = (valueA, valueB, a, b, inverted) => {
  // if (!strLookLikeDDMMYYYYDate(valueA) || !strLookLikeDDMMYYYYDate(valueB)) return 0

  var date1Number = getDDMMYYYYWeight(valueA)
  var date2Number = getDDMMYYYYWeight(valueB)
  if (date1Number === null && date2Number === null) return 0
  if (date1Number === null) return -1
  if (date2Number === null) return 1

  return date1Number - date2Number
}

/**
 * Given a list of columns from widgetUtils, return a list of formatted column
 * @param [columns] - an array of objects that describe the columns in the table.
 * @param options
 * @returns The formatted columns for the table.
 */
const makeCustomSortComparator = (sortOrder) => (valueA, valueB, _nodeA, _nodeB, isDescending) => {
  const aEmpty = valueA == null || valueA === ""
  const bEmpty = valueB == null || valueB === ""

  // Valeurs vides toujours en fin, quelle que soit la direction
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return isDescending ? -1 : 1
  if (bEmpty) return isDescending ? 1 : -1

  const idxA = sortOrder.indexOf(String(valueA))
  const idxB = sortOrder.indexOf(String(valueB))
  const a = idxA === -1 ? sortOrder.length : idxA
  const b = idxB === -1 ? sortOrder.length : idxB
  return a - b
}

const testThreshold = (v, op, t) => {
  switch (op) {
    case ">": return v > t
    case ">=": return v >= t
    case "<": return v < t
    case "<=": return v <= t
    case "==": return v === t
    case "!=": return v !== t
    default: return false
  }
}

/**
 * Style de cellule (fond) selon des seuils numériques configurables. `thresholds` =
 * [{ operator, value, color }] (operator ∈ > >= < <= == !=). La **première règle satisfaite** (dans
 * l'ordre) l'emporte ; aucune → fond par défaut (blanc). Rétro-compatible avec l'ancien format
 * `{ over, color }` (interprété comme `> over`).
 */
export const getThresholdCellStyle = (value, thresholds) => {
  const v = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(",", "."))
  if (!Array.isArray(thresholds) || isNaN(v)) return null
  for (const r of thresholds) {
    if (!r) continue
    const op = r.operator || ">"
    const t = r.value != null ? Number(r.value) : Number(r.over)
    if (!isNaN(t) && r.color && testThreshold(v, op, t)) return { backgroundColor: r.color }
  }
  return null
}

export const getColumnsProps = (columns = [], options) => {
  const formattedColumns = columns.map((column, index) => {
    const { type, label, valueId, displayPercentSymbolAfterValue, displayAsPercent, colorThresholds, uuid, customSortOrder, customSortEnabled } = column

    const columnType = getColumnType(type)
    const isFirstColumnAndFreeze = index === 0 && options?.freezeFirstColumn
    const useCustomSort = customSortEnabled === true && customSortOrder?.length > 0
    return {
      uuid: uuid,
      field: valueId,
      filter: options?.enableFilters ? getFilterFormat(columnType) : false,
      headerName: label,
      type: columnType,
      valueFormatter: ({ value }) =>
        getCellFormat(value, columnType, { ...options, displayPercentSymbolAfterValue, displayAsPercent }),
      sortable: true,
      // Mise en forme conditionnelle par seuils numériques (heatmap) : fond de cellule selon la valeur.
      ...(Array.isArray(colorThresholds) && colorThresholds.length > 0 && {
        cellStyle: params => getThresholdCellStyle(params?.value, colorThresholds),
      }),
      ...(useCustomSort && { comparator: makeCustomSortComparator(customSortOrder) }),
      ...(!useCustomSort && type === "Date" && { comparator: dateComparator }),
      ...(isFirstColumnAndFreeze && { pinned: "left" }),
    }
  })

  const countRows = {
    headerName: "",
    maxWidth: 100,
    valueFormatter: params => `${parseInt(params.node.id) + 1}`,
  }

  options?.enableCountRows && formattedColumns.unshift(countRows)

  return formattedColumns
}

/** Nombre lu dans une cellule — y compris un ratio déjà formaté en texte (« 45,3 % »). */
const nombreDeCellule = valeur => {
  if (typeof valeur === "number") return Number.isFinite(valeur) ? valeur : null
  if (typeof valeur !== "string" || valeur.trim() === "") return null
  const n = parseFloat(valeur.replace(/\s/g, "").replace(",", "."))
  return Number.isFinite(n) ? n : null
}

/**
 * Ligne de pied CONFIGURÉE colonne par colonne : `aggregations` = `{ [champ]: "sum" | "mean" }`.
 * Seules les colonnes listées sont calculées ; `label` s'écrit dans la première colonne.
 *
 * Existe parce que le pied historique (`getColumnsSumRow` sans options) SOMME toutes les colonnes
 * numériques : sur un tableau de taux par entité, il additionnait des pourcentages et ignorait les
 * ratios, rendus en texte. Demande CRC du 13/10/2026 : une ligne « Taux de remontée moyen » portant
 * la moyenne de la dernière colonne.
 *
 * ⚠️ « mean » est la moyenne des CELLULES (une entité = un poids), pas un taux recalculé sur les
 * totaux : c'est ce qui est demandé, et la vignette du dashboard porte déjà le taux global.
 */
export const getConfiguredFooterRow = (columns, data, { aggregations, label }, numberDecimal = 0, showSpaceForThousand = true) => {
  const ligne = {}
  if (!Array.isArray(columns) || !Array.isArray(data)) return ligne
  const formater = n =>
    showSpaceForThousand
      ? n.toLocaleString("fr", { minimumFractionDigits: numberDecimal, maximumFractionDigits: numberDecimal })
      : n.toFixed(numberDecimal)

  columns.forEach(col => {
    const mode = col?.field ? aggregations?.[col.field] : undefined
    if (mode !== "sum" && mode !== "mean") return
    const cellules = data.map(row => row?.[col.field])
    const nombres = cellules.map(nombreDeCellule).filter(n => n !== null)
    if (!nombres.length) return
    const somme = nombres.reduce((acc, n) => acc + n, 0)
    const valeur = mode === "mean" ? somme / nombres.length : somme
    // Un ratio rendu « 45,3 % » garde son unité dans le pied.
    const enPourcent = cellules.some(c => typeof c === "string" && c.includes("%"))
    ligne[col.field] = `${formater(valeur)}${enPourcent ? " %" : ""}`
  })

  const premiere = columns.find(col => col?.field)?.field
  if (label && premiere && ligne[premiere] === undefined) ligne[premiere] = label
  return ligne
}

export const getColumnsSumRow = (columns, data, numberDecimal = 0, showSpaceForThousand = true) => {
  if (!Array.isArray(columns) || !Array.isArray(data)) return {}
  let sums = {}
  columns.forEach(col => {
    if (!col?.field) return
    if (col?.type === "date") return
    const fieldId = col.field
    const dataAssociate = data.map(item => item[fieldId])
    const dataFilteredNumber = dataAssociate.filter(data => !isNaN(data) && data)
    if (!dataFilteredNumber.length) return
    const sum = dataFilteredNumber.reduce((acc, val) => acc + parseFloat(val), 0)
    if (sum === 0 || isNaN(sum)) return
    let formattedSum
    if (showSpaceForThousand) {
      formattedSum = sum.toLocaleString("fr", {
        minimumFractionDigits: numberDecimal,
        maximumFractionDigits: numberDecimal,
      })
    } else {
      formattedSum = sum.toFixed(numberDecimal)
    }
    sums[fieldId] = `Total: ${formattedSum}`
  })
  return sums
}
